import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { fetchAuthSession } from "aws-amplify/auth";
import { awsConfig } from "../aws-config";
import { getAuthHeader, getProProfile, getUploadUrl, uploadFileToS3, getServices } from "../api";

// Categories loaded from API
const RESPONSE_TIMES = ["30 minutes","1 hour","2 hours","Same day"];
const EXPERIENCE_OPTIONS = ["1-2 Years","3-5 Years","5-8 Years","8-10 Years","10+ Years","15+ Years"];

export default function ProProfileEditPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [activeSection, setActiveSection] = useState("basic");
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState("");

  const [services, setServices] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>([]);

  const [form, setForm] = useState({
    name: "", businessName: "", bio: "", serviceCategory: "", subcategories: [] as string[], pausedSubcategories: [] as string[], hourlyRate: "", serviceRadius: "25",
    city: "", experience: "", responseTime: "1 hour",
    phone: "", photoUrl: "", yearsInBusiness: "", employees: "",
    languages: "English", certifications: "", businessHours: "Mon-Fri 8am-6pm",
    about: "", serviceArea: "", website: "", instagram: ""
  });

  useEffect(() => {
    getServices().then(s => setServices(s || [])).catch(() => {});

    getProProfile()
      .then((profile: any) => {
        console.log("Profile loaded:", profile);
        if (profile) {
          setForm({
            name: profile.name || "",
            bio: profile.bio || "",
            businessName: profile.businessName || "",
            serviceCategory: profile.serviceCategory || "",
            pausedSubcategories: profile.pausedSubcategories || [],
            subcategories: profile.subcategories || [],
            serviceRadius: profile.serviceRadius?.toString() || "25",
            hourlyRate: profile.hourlyRate?.toString() || "",
            city: profile.city || "",
            experience: profile.experience || "",
            responseTime: profile.responseTime || "1 hour",
            phone: profile.phone || "",
            photoUrl: profile.photoUrl || "",
            yearsInBusiness: profile.yearsInBusiness || "",
            employees: profile.employees || "",
            languages: profile.languages || "English",
            certifications: profile.certifications || "",
            businessHours: profile.businessHours || "Mon-Fri 8am-6pm",
            about: profile.about || "",
            serviceArea: profile.serviceArea || "",
            website: profile.website || "",
            instagram: profile.instagram || "",
          });
          if (profile.photoUrl) setPhotoPreview(profile.photoUrl);
        }
      })
      .catch(err => console.error("Failed to load profile:", err))
      .finally(() => setLoading(false));
  }, []);

  function update(field: string, value: string) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    setSaving(true);
    setError("");
    console.log("Saving form:", form);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      console.log("Token:", token ? "exists" : "missing");

      let photoUrl = form.photoUrl;
      if (photo) {
        const { uploadUrl, fileUrl } = await getUploadUrl(photo.name, photo.type, "profile-photos");
        await uploadFileToS3(uploadUrl, photo);
        photoUrl = fileUrl;
      }

      await axios.put(
        `${awsConfig.apiUrl}/pro/profile`,
        { ...form, hourlyRate: parseFloat(form.hourlyRate) || 0, photoUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error("Save error:", err);
      console.error("Response:", err.response?.data);
      console.error("Status:", err.response?.status);
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-mouna-blue border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const sections = [
    { id: "basic", label: "Basic Info" },
    { id: "business", label: "Business" },
    { id: "contact", label: "Contact" },
    { id: "social", label: "Social" },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white px-5 pt-4 pb-4 shadow-sm">
        <div className="flex items-center justify-between mb-1">
          <button onClick={() => navigate("/home")} className="text-gray-400 text-sm">← Dashboard</button>
          <button onClick={handleSave} disabled={saving}
            className="bg-mouna-blue text-white rounded-full px-5 py-2 text-sm font-bold">
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
        <h1 className="font-display text-2xl font-bold text-navy mt-2">Edit Profile</h1>
      </div>

      {error && <div className="mx-5 mt-3 bg-red-50 text-red-500 text-sm rounded-xl p-3">{error}</div>}
      {success && <div className="fixed top-4 left-4 right-4 bg-teal text-white text-sm rounded-xl p-4 font-bold text-center z-50 shadow-lg">Profile saved successfully!</div>}

      {/* Section tabs */}
      <div className="bg-white border-b border-gray-100 px-5 flex gap-3 overflow-x-auto mt-1">
        {sections.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            className={"py-3 text-xs font-bold uppercase tracking-wider whitespace-nowrap border-b-2 transition-all " +
              (activeSection === s.id ? "border-mouna-blue text-mouna-blue" : "border-transparent text-gray-400")}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="px-5 pt-4 pb-24 space-y-4">

        {activeSection === "basic" && (
          <>
            {/* Photo */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <div className="font-bold text-navy mb-3">Profile Photo</div>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-2xl bg-blue-pale overflow-hidden flex items-center justify-center flex-shrink-0">
                  {photoPreview
                    ? <img src={photoPreview} alt="preview" className="w-full h-full object-cover"/>
                    : <span className="text-3xl">👷</span>}
                </div>
                <div>
                  <input type="file" accept="image/*" id="photo-input" className="hidden"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) { setPhoto(file); setPhotoPreview(URL.createObjectURL(file)); }
                    }}/>
                  <label htmlFor="photo-input" className="bg-mouna-blue text-white rounded-full px-4 py-2 text-xs font-bold cursor-pointer">
                    Change Photo
                  </label>
                  <div className="text-xs text-gray-400 mt-1">JPG or PNG, max 5MB</div>
                </div>
              </div>
            </div>

            {/* Name */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">Full Name</label>
              <input type="text" value={form.name} onChange={e => update("name", e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-mouna-blue text-navy"/>
              <div className="text-xs text-gray-400 mt-1.5">Used for verification only — not displayed publicly</div>
            </div>
            {/* Business Name */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">Business Name</label>
              <input type="text" value={form.businessName} onChange={e => update("businessName", e.target.value)}
                placeholder="e.g. Thorne Electrical Services"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-mouna-blue text-navy"/>
              <div className="text-xs text-gray-400 mt-1.5">This is what clients see on your profile</div>
            </div>
            {/* Category - Premium Expandable System */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-3 block">Service Category</label>
              {form.serviceCategory && (
                <div className="bg-blue-pale rounded-xl px-3 py-2 mb-3 flex items-center justify-between">
                  <span className="text-sm font-bold text-mouna-blue">
                    {services.find((s: any) => s.name === form.serviceCategory)?.icon} {form.serviceCategory}
                  </span>
                  <button onClick={() => { update("serviceCategory", ""); setForm((prev: any) => ({ ...prev, subcategories: [] })); }}
                    className="text-xs text-gray-400 hover:text-red-500">Clear</button>
                </div>
              )}
              <div className="relative mb-3">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
                <input type="text" placeholder="Search categories..."
                  value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm outline-none focus:border-mouna-blue transition-colors"/>
              </div>
              <div className="space-y-1.5 max-h-96 overflow-y-auto">
                {services
                  .filter((svc: any) => selectedCategory === "" || svc.name.toLowerCase().includes(selectedCategory.toLowerCase()))
                  .map((svc: any) => {
                    const isSelected = form.serviceCategory === svc.name;
                    const isExpanded = isSelected;
                    const activeSubs = (form.subcategories || []);
                    const pausedSubs = (form.pausedSubcategories || []);
                    return (
                      <div key={svc.categoryId} className={"rounded-2xl border-2 overflow-hidden transition-all " + (isSelected ? "border-mouna-blue" : "border-gray-100")}>
                        {/* Category header */}
                        <button onClick={() => { update("serviceCategory", svc.name); if (!isSelected) { setForm((prev: any) => ({ ...prev, subcategories: [], pausedSubcategories: [] })); } setSelectedCategory(""); }}
                          className={"w-full flex items-center gap-3 px-4 py-3 text-left transition-colors " + (isSelected ? "bg-mouna-blue" : "bg-white hover:bg-gray-50")}>
                          <span className="text-xl flex-shrink-0">{svc.icon}</span>
                          <div className="flex-1 min-w-0">
                            <div className={"font-semibold text-sm " + (isSelected ? "text-white" : "text-navy")}>{svc.name}</div>
                            <div className={"text-xs " + (isSelected ? "text-blue-200" : "text-gray-400")}>
                              {isSelected ? (activeSubs.length + " active · " + pausedSubs.length + " paused") : (svc.subcategories?.length || 0) + " services available"}
                            </div>
                          </div>
                          <span className={"text-sm transition-transform " + (isSelected ? "text-white rotate-180" : "text-gray-400")}>▾</span>
                        </button>

                        {/* Subcategory dropdown */}
                        {isExpanded && svc.subcategories?.length > 0 && (
                          <div className="border-t border-blue-100 bg-gray-50 p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">Services</span>
                              <div className="flex gap-2">
                                <button onClick={() => setForm((prev: any) => ({ ...prev, subcategories: svc.subcategories, pausedSubcategories: [] }))}
                                  className="text-xs text-teal font-bold">Enable All</button>
                                <button onClick={() => setForm((prev: any) => ({ ...prev, subcategories: [], pausedSubcategories: svc.subcategories }))}
                                  className="text-xs text-gray-400 font-bold">Pause All</button>
                              </div>
                            </div>
                            <div className="space-y-1.5">
                              {svc.subcategories.map((sub: string) => {
                                const isActive = activeSubs.includes(sub);
                                const isPaused = pausedSubs.includes(sub);
                                return (
                                  <div key={sub} className={"flex items-center justify-between px-3 py-2.5 rounded-xl border transition-all " +
                                    (isActive ? "bg-white border-teal border-opacity-50" : isPaused ? "bg-yellow-50 border-yellow-200" : "bg-white border-gray-200")}>
                                    <div className="flex items-center gap-2">
                                      <div className={"w-2 h-2 rounded-full flex-shrink-0 " + (isActive ? "bg-teal" : isPaused ? "bg-yellow-400" : "bg-gray-300")}></div>
                                      <span className={"text-sm font-medium " + (isActive ? "text-navy" : isPaused ? "text-yellow-700" : "text-gray-400")}>{sub}</span>
                                    </div>
                                    <div className="flex gap-1.5">
                                      {!isActive && (
                                        <button onClick={() => {
                                          const newActive = [...activeSubs.filter((s: string) => s !== sub), sub];
                                          const newPaused = pausedSubs.filter((s: string) => s !== sub);
                                          setForm((prev: any) => ({ ...prev, subcategories: newActive, pausedSubcategories: newPaused }));
                                        }} className="text-xs bg-teal-light text-teal font-bold rounded-full px-2.5 py-1">+ Add</button>
                                      )}
                                      {isActive && (
                                        <button onClick={() => {
                                          const newActive = activeSubs.filter((s: string) => s !== sub);
                                          const newPaused = [...pausedSubs, sub];
                                          setForm((prev: any) => ({ ...prev, subcategories: newActive, pausedSubcategories: newPaused }));
                                        }} className="text-xs bg-yellow-50 text-yellow-600 font-bold rounded-full px-2.5 py-1">⏸ Pause</button>
                                      )}
                                      {isPaused && (
                                        <button onClick={() => {
                                          const newPaused = pausedSubs.filter((s: string) => s !== sub);
                                          setForm((prev: any) => ({ ...prev, pausedSubcategories: newPaused }));
                                        }} className="text-xs bg-red-50 text-red-400 font-bold rounded-full px-2 py-1">✕</button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
            {/* Summary of active services */}
            {(form.subcategories || []).length > 0 && (
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs text-gray-400 font-bold uppercase tracking-wider">Your Active Services</label>
                  <span className="badge badge-green">{(form.subcategories || []).length} active</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(form.subcategories || []).map((sub: string) => (
                    <span key={sub} className="flex items-center gap-1.5 bg-teal-light text-teal text-xs font-semibold rounded-full px-3 py-1.5">
                      <div className="w-1.5 h-1.5 bg-teal rounded-full"></div>
                      {sub}
                    </span>
                  ))}
                </div>
                {(form.pausedSubcategories || []).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="text-xs text-yellow-600 font-bold mb-2">⏸ Paused ({(form.pausedSubcategories || []).length})</div>
                    <div className="flex flex-wrap gap-2">
                      {(form.pausedSubcategories || []).map((sub: string) => (
                        <span key={sub} className="bg-yellow-50 text-yellow-700 text-xs font-semibold rounded-full px-3 py-1.5">{sub}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Bio */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">Short Bio</label>
              <textarea value={form.bio} onChange={e => update("bio", e.target.value)} rows={3}
                placeholder="Brief description shown on your card..."
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-mouna-blue resize-none text-navy"/>
            </div>

            {/* Hourly Rate */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">Hourly Rate (USD)</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</span>
                <input type="number" value={form.hourlyRate} onChange={e => update("hourlyRate", e.target.value)}
                  className="w-full border border-gray-200 rounded-xl pl-8 pr-4 py-3 text-sm outline-none focus:border-mouna-blue text-navy"/>
              </div>
            </div>

            {/* Experience */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-3 block">Years of Experience</label>
              <div className="grid grid-cols-3 gap-2">
                {EXPERIENCE_OPTIONS.map(exp => (
                  <button key={exp} onClick={() => update("experience", exp)}
                    className={"py-2 px-2 rounded-xl text-xs font-semibold border-2 transition-all " +
                      (form.experience === exp ? "bg-mouna-blue text-white border-mouna-blue" : "bg-white text-gray-500 border-gray-200")}>
                    {exp}
                  </button>
                ))}
              </div>
            </div>

            {/* Response time */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-3 block">Response Time</label>
              <div className="grid grid-cols-2 gap-2">
                {RESPONSE_TIMES.map(rt => (
                  <button key={rt} onClick={() => update("responseTime", rt)}
                    className={"py-2 px-3 rounded-xl text-xs font-semibold border-2 transition-all " +
                      (form.responseTime === rt ? "bg-mouna-blue text-white border-mouna-blue" : "bg-white text-gray-500 border-gray-200")}>
                    {rt}
                  </button>
                ))}
              </div>
            </div>

            {/* City */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">City</label>
              <input type="text" value={form.city} onChange={e => update("city", e.target.value)}
                placeholder="e.g. New York"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-mouna-blue text-navy"/>
            </div>

            {/* Service Area */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">Service Area</label>
              <input type="text" value={form.serviceArea} onChange={e => update("serviceArea", e.target.value)}
                placeholder="e.g. Greater Houston Area, 30 mile radius"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-mouna-blue text-navy"/>
              <div className="mt-3">
                <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">Service Radius (miles)</label>
                <div className="flex gap-2">
                  {["5", "10", "15", "25", "35", "50"].map(r => (
                    <button key={r} onClick={() => update("serviceRadius", r)}
                      className={"flex-1 py-2 rounded-xl text-xs font-bold border-2 " +
                        (form.serviceRadius === r ? "bg-mouna-blue text-white border-mouna-blue" : "bg-white text-gray-500 border-gray-200")}>
                      {r}mi
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {activeSection === "business" && (
          <>
            {/* About */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">About Your Business</label>
              <textarea value={form.about} onChange={e => update("about", e.target.value)} rows={5}
                placeholder="Tell your story — why you do what you do, what makes you special..."
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-mouna-blue resize-none text-navy"/>
            </div>

            {/* Years in business */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">Years in Business</label>
              <input type="text" value={form.yearsInBusiness} onChange={e => update("yearsInBusiness", e.target.value)}
                placeholder="e.g. 5 years"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-mouna-blue text-navy"/>
            </div>

            {/* Employees */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">Number of Employees</label>
              <input type="text" value={form.employees} onChange={e => update("employees", e.target.value)}
                placeholder="e.g. Just me, 2-5, 10+"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-mouna-blue text-navy"/>
            </div>

            {/* Business Hours */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">Business Hours</label>
              <input type="text" value={form.businessHours} onChange={e => update("businessHours", e.target.value)}
                placeholder="e.g. Mon-Fri 8am-6pm, Sat 9am-2pm"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-mouna-blue text-navy"/>
            </div>

            {/* Languages */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">Languages Spoken</label>
              <input type="text" value={form.languages} onChange={e => update("languages", e.target.value)}
                placeholder="e.g. English, Spanish"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-mouna-blue text-navy"/>
            </div>

            {/* Certifications */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">Certifications & Licenses</label>
              <textarea value={form.certifications} onChange={e => update("certifications", e.target.value)} rows={3}
                placeholder="e.g. Licensed Electrician #12345, OSHA Certified..."
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-mouna-blue resize-none text-navy"/>
            </div>
          </>
        )}

        {activeSection === "contact" && (
          <>
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">Phone Number</label>
              <input type="tel" value={form.phone} onChange={e => update("phone", e.target.value)}
                placeholder="+1 (555) 000-0000"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-mouna-blue text-navy"/>
            </div>
          </>
        )}

        {activeSection === "social" && (
          <>
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">Website</label>
              <input type="url" value={form.website} onChange={e => update("website", e.target.value)}
                placeholder="https://yourwebsite.com"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-mouna-blue text-navy"/>
            </div>
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">Instagram</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">@</span>
                <input type="text" value={form.instagram} onChange={e => update("instagram", e.target.value)}
                  placeholder="yourusername"
                  className="w-full border border-gray-200 rounded-xl pl-8 pr-4 py-3 text-sm outline-none focus:border-mouna-blue text-navy"/>
              </div>
            </div>
          </>
        )}

        <button onClick={() => navigate("/pro/availability")}
          className="w-full bg-gray-100 text-gray-600 rounded-full py-4 font-bold text-sm mb-3">
          📅 Set Availability
        </button>
        <button onClick={handleSave} disabled={saving}
          className="w-full bg-mouna-blue text-white rounded-full py-4 font-bold text-sm">
          {saving ? "Saving..." : "Save Profile"}
        </button>
      </div>
    </div>
  );
}
